import React, {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react'
import { Button, Checkbox, Divider, Input } from 'antd'

import classNames from 'classnames'

import styles from './style.module.scss'

import chevronUp24 from 'assets/images/chevron-up-gray2-24.svg'
import chevronUp24Disabled from 'assets/images/chevron-up-gray2-disabled-24.svg'
import chevronDown24 from 'assets/images/chevron-down-gray2-24.svg'
import chevronDown24Disabled from 'assets/images/chevron-down-gray2-disabled-24.svg'
import toolbarX24SVG from 'assets/images/toolbar-x-gray-24.svg'
import { findTextRanges } from './utils/findTextRanges'
import findTextObserver from './utils/findTextObserver'
import { getSharedHighlight } from './utils/sharedHighlight'
import { useAccumulateMemo } from '../hooks/useAccumulateMemo'
import { persistRangeReference } from './utils/persistRangeReference'
import { useTranslation } from 'react-i18next'
import { composeRefs } from './utils/composeRefs'
import { isRangeVisible } from './utils/isRangeVisible'
import { isRangeAccessibility } from './utils/isRangeAccessibility'
import { findScrollableParent } from 'utils'

interface FindableNode {
  node: Node
  shallow: boolean
}

interface RangesInNode {
  node: Node
  ranges: Range[]
}

interface FocusedRange {
  node: Node
  range: Range
}

export interface FocusInfo {
  focused: boolean
  visible: boolean
}

interface TextFinderContextValue {
  text: string | null
  handleTextChange: (text: string | null) => void
  registerFindableNode: (findableNode: FindableNode) => (() => void) | undefined
  moveFocus: (step: number) => void
  focused: FocusedRange | undefined
  rangesInNodes: RangesInNode[]
  bodySearch: boolean
  setBodySearch: (value: boolean) => void
}

const TextFinderContext = createContext<TextFinderContextValue | null>(null)

interface TextFinderProviderProps {
  children: ReactNode
  // 포커스된 검색 결과로 스크롤 이동할지 여부. ThreadList처럼 한 줄 ellipsis로
  // 표시되는 영역에서는 스크롤 이동 시 레이아웃이 깨져 보이므로 false로 끈다.
  autoScrollToFocused?: boolean
}

export const TextFinderProvider = ({
  children,
  autoScrollToFocused = true,
}: TextFinderProviderProps) => {
  const [text, setText] = useState<string | null>(null)
  const [findableNodes, setFindableNodes] = useState<FindableNode[]>([])
  const [focused, setFocused] = useState<FocusedRange>()
  const [bodySearch, setBodySearch] = useState(false)

  const deferredText = useDeferredValue(text)

  const rangesInNodes = useAccumulateMemo(
    (prev: RangesInNode[]) => {
      if (!deferredText) {
        return []
      }
      return findableNodes
        .map((findableNode) => ({
          node: findableNode.node,
          ranges: findTextRanges(findableNode.node, deferredText.split(' '))
            .filter((range) => !isRangeAccessibility(range))
            .filter((range) => !findableNode.shallow || isRangeVisible(range)),
        }))
        .map((rangesInNode) => persistRangeReference(rangesInNode, prev))
    },
    [findableNodes, deferredText],
  )

  const focusableRanges = useMemo(() => {
    return rangesInNodes
      .filter(({ ranges }) => ranges.length > 0)
      .flatMap(({ node, ranges }) => ranges.map((range) => ({ node, range })))
  }, [rangesInNodes])

  const handleTextChange = useCallback((text: string | null) => setText(text), [setText])

  const moveFocus = useCallback(
    (step: number) => {
      if (!focusableRanges.length) return

      const totalCount = focusableRanges.length
      let nextIndex: number

      if (!focused) {
        nextIndex = step > 0 ? step - 1 : totalCount + step
      } else {
        nextIndex = focusableRanges.findIndex(({ range }) => range === focused.range) + step
      }

      nextIndex = ((nextIndex % totalCount) + totalCount) % totalCount
      /*
       * NOTE
       * moveFocus 시 동일한 range를 다시 선택하는 경우에,
       * 스크롤 이동을 동작하도록 하기 위해 새로운 객체를 생성해서 전달
       */
      setFocused({ ...focusableRanges[nextIndex] })
    },
    [focusableRanges, focused, setFocused],
  )

  const registerFindableNode = useCallback(
    (findableNode: FindableNode) => {
      if (!findableNode.node) return

      setFindableNodes((p) => {
        // 빠른 전환 시 unregister가 누락돼 detached 노드가 잔류하는 것을 방지 —
        // 등록 시점에 연결이 끊긴 노드를 prune한다.
        const connected = p.filter(({ node }) => (node as Node).isConnected)
        if (connected.some(({ node }) => node === findableNode.node)) return connected

        return [...connected, findableNode].sort((a, b) =>
          a.node.compareDocumentPosition(b.node) & Node.DOCUMENT_POSITION_PRECEDING ? 1 : -1,
        )
      })

      return () =>
        setFindableNodes((p) =>
          p.filter(({ node }) => node !== findableNode.node && (node as Node).isConnected),
        )
    },
    [setFindableNodes],
  )

  const contextValue = useMemo<TextFinderContextValue>(
    () => ({
      text,
      handleTextChange,
      registerFindableNode,
      moveFocus,
      focused,
      rangesInNodes,
      bodySearch,
      setBodySearch,
    }),
    [
      text,
      handleTextChange,
      focused,
      rangesInNodes,
      registerFindableNode,
      moveFocus,
      bodySearch,
      setBodySearch,
    ],
  )

  useEffect(() => {
    setFocused(focusableRanges[0])
  }, [deferredText])

  // 이름별 공유 Highlight에 이 provider의 Range만 add/delete 한다.
  // clear()로 레지스트리 전체를 비우지 않으므로 다른 provider의 하이라이트를 건드리지 않는다.
  useEffect(() => {
    const highlight = getSharedHighlight('findable-text')
    if (highlight == null) return

    const ranges = rangesInNodes.flatMap(({ ranges }) => ranges)
    ranges.forEach((range) => highlight.add(range))

    return () => {
      ranges.forEach((range) => highlight.delete(range))
    }
  }, [rangesInNodes])

  useEffect(() => {
    const focusHighlight = getSharedHighlight('focused-findable-text')
    if (focusHighlight == null || !focused) return

    focusHighlight.add(focused.range)

    return () => {
      focusHighlight.delete(focused.range)
    }
  }, [focused])

  useEffect(
    function scrollToFocused() {
      if (!autoScrollToFocused || !focused) return

      const timer = setTimeout(() => {
        const scrollContainer = findScrollableParent(
          focused.range.commonAncestorContainer as HTMLElement,
        )
        const containerRect = scrollContainer.getBoundingClientRect()
        const rangeRect = focused.range.getBoundingClientRect()

        const scrollTop =
          scrollContainer.scrollTop + (rangeRect.top - containerRect.top) - containerRect.height / 2

        scrollContainer.scrollTo({
          top: scrollTop,
          behavior: 'smooth',
        })
      })

      return () => clearTimeout(timer)
    },
    [focused, autoScrollToFocused],
  )

  useEffect(() => {
    // replay: true — 검색 후 뒤늦게 mount되는 provider도 현재 검색어를 즉시 받아 하이라이트를 계산한다.
    const cleanup = findTextObserver.register(handleTextChange, { replay: true })
    return () => {
      cleanup()
    }
  }, [handleTextChange])

  return <TextFinderContext.Provider value={contextValue}>{children}</TextFinderContext.Provider>
}

export const useTextFinderContext = (): TextFinderContextValue => {
  const context = useContext(TextFinderContext)
  if (!context) {
    throw new Error('useTextFinderContext must be used within a TextFinderProvider')
  }
  return context
}

interface TextFinderObserverProps {
  as?: React.ElementType
  onContains?: (isContains: boolean) => void
  onFocus?: (focusInfo: FocusInfo) => void
  disabled?: boolean
  [key: string]: any
}

export const TextFinderObserver = ({
  as,
  onContains,
  onFocus,
  disabled = false,
  ...props
}: TextFinderObserverProps) => {
  const Component = as || 'div'
  const { text, rangesInNodes, focused } = useTextFinderContext()
  const nodeRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (disabled || !text) return

    const ranges = rangesInNodes.flatMap(({ ranges }) => ranges)
    const isContains = ranges.some(({ startContainer, endContainer }) => {
      return nodeRef.current?.contains(startContainer) || nodeRef.current?.contains(endContainer)
    })

    onContains?.(isContains)
  }, [text, rangesInNodes, disabled, onContains])

  useEffect(() => {
    if (disabled || !focused) return

    const isFocused =
      nodeRef.current?.contains(focused.range.startContainer) ||
      nodeRef.current?.contains(focused.range.endContainer)

    onFocus?.({
      focused: isFocused,
      visible: isFocused && isRangeVisible(focused.range),
    })
  }, [focused, disabled, onFocus])

  return <Component ref={nodeRef} {...props} />
}

interface TextFindableNodeProps {
  onFocusChange?: (focusInfo: FocusInfo) => void
  onMount?: () => void
  as?: React.ElementType
  shallow?: boolean
  disabled?: boolean
  [key: string]: any
}

export const TextFindableNode = ({
  onFocusChange,
  onMount,
  as,
  shallow = false,
  disabled = false,
  ...props
}: TextFindableNodeProps) => {
  const Component = as || 'div'
  const { registerFindableNode, focused } = useTextFinderContext()
  const nodeRef = useRef<HTMLElement | null>(null)
  const [textContent, setTextContent] = useState<string | null>(null)

  // render 중이 아니라 커밋 후 DOM에서 textContent를 읽는다.
  // render 시점 읽기는 커밋 전(이전) 값이라, 텍스트가 in-place로 갱신되는 재렌더에서
  // 의존성이 한 render 늦게 반영돼 Range 재계산이 누락되고 하이라이트가 사라진다.
  // (같은 값이면 setState bail-out으로 재렌더가 발생하지 않아 무한 루프 없음.)
  useLayoutEffect(() => {
    setTextContent(nodeRef.current?.textContent ?? null)
  })

  useEffect(() => {
    if (disabled) return

    const unregister = registerFindableNode({ node: nodeRef.current as Node, shallow })
    return () => unregister?.()
  }, [registerFindableNode, textContent, shallow, disabled])

  useEffect(() => {
    if (disabled || !focused) return

    const isFocused =
      nodeRef.current?.contains(focused.range.startContainer) ||
      nodeRef.current?.contains(focused.range.endContainer)

    onFocusChange?.({
      focused: isFocused,
      visible: isFocused && isRangeVisible(focused.range),
    })
  }, [focused, disabled, onFocusChange])

  return <Component ref={nodeRef} {...props} />
}

interface TextFinderSearchBarProps {
  onClose: () => void
}

export const TextFinderSearchBar = forwardRef<HTMLInputElement, TextFinderSearchBarProps>(
  ({ onClose }, ref) => {
    const { text, handleTextChange, focused, moveFocus, rangesInNodes, bodySearch, setBodySearch } =
      useTextFinderContext()
    const inputRef = useRef<any>(null)
    const { t } = useTranslation()
    const [internalText, setInternalText] = useState<string | null>(text)

    const currentIndex =
      rangesInNodes.flatMap(({ ranges }) => ranges).findIndex((range) => range === focused?.range) +
      1
    const totalCount = rangesInNodes.reduce((acc, { ranges }) => acc + ranges.length, 0)

    useEffect(() => {
      return () => handleTextChange(null)
    }, [handleTextChange])

    useEffect(() => {
      const closeOnEscape = (e: KeyboardEvent) => !e.isComposing && e.key === 'Escape' && onClose()

      window.addEventListener('keydown', closeOnEscape)
      return () => window.removeEventListener('keydown', closeOnEscape)
    }, [onClose])

    useEffect(() => {
      const handleEnter = (e: KeyboardEvent) => {
        if (!e.isComposing && e.key === 'Enter') {
          if (internalText !== text) {
            return handleTextChange(internalText)
          }

          e.shiftKey ? moveFocus(-1) : moveFocus(1)
        }
      }

      window.addEventListener('keydown', handleEnter)
      return () => window.removeEventListener('keydown', handleEnter)
    }, [moveFocus, internalText, text, handleTextChange])

    useEffect(() => {
      const cleanup = findTextObserver.register((text) => setInternalText(text))
      return () => {
        cleanup()
      }
    }, [])

    return (
      <div className={classNames(styles.textFinder, 'textFinder')}>
        <div className={styles.inputWrapper}>
          <Input
            ref={composeRefs(ref, inputRef)}
            value={internalText || ''}
            onChange={(e) => setInternalText(e.target.value)}
            onCut={(e: React.ClipboardEvent<HTMLInputElement>) => {
              if (!internalText) return

              e.preventDefault()
              const selectedText = window.getSelection()?.toString() || ''
              const target = e.target as HTMLInputElement
              const start = target.selectionStart || 0
              const end = target.selectionEnd || 0

              navigator.clipboard.writeText(selectedText)

              setInternalText(internalText.slice(0, start) + internalText.slice(end))

              setTimeout(() => target.setSelectionRange(start, start))
            }}
            variant="borderless"
          />
          {<span className={styles.count}>{text?.trim() && `${currentIndex}/${totalCount}`}</span>}
        </div>

        <Divider type="vertical" style={{ alignSelf: 'stretch', height: 'auto', margin: 0 }} />

        <Checkbox
          className={styles.checkbox}
          checked={bodySearch}
          onChange={(e) => setBodySearch(e.target.checked)}
        >
          {t('email.button.allEmail')}
        </Checkbox>

        <Button
          type="text"
          size="small"
          icon={
            <img
              src={totalCount ? chevronUp24 : chevronUp24Disabled}
              alt="go-to-prev"
              onClick={() => moveFocus(-1)}
            />
          }
          disabled={!totalCount}
        />
        <Button
          type="text"
          size="small"
          icon={
            <img
              src={totalCount ? chevronDown24 : chevronDown24Disabled}
              alt="go-to-next"
              onClick={() => moveFocus(1)}
            />
          }
          disabled={!totalCount}
        />
        <Button
          type="text"
          size="small"
          icon={<img src={toolbarX24SVG} alt="close-text-finder" />}
          onClick={onClose}
        />
      </div>
    )
  },
)

TextFinderSearchBar.displayName = 'TextFinderSearchBar'
